import {
  Assembly,
  PointOfInterest,
  useGetAssembliesWithTemplateFieldDataLazyQuery,
  useGetAssemblyContextualizedStatusQuery,
  useSelectedPointOfInterestQuery,
} from '@/__generated__/graphql';
import * as customBlockState from '@/components/Analysis/modules/CustomBlocks/state';
import * as state from '@/components/Analysis/state';
import { useSelectedAssemblyTemplateId } from '@/components/Analysis/hooks/useSelectedAssemblyTemplateId';
import {
  selectedFilteredDeckIds as selectedFilteredDeckIdsState,
  selectedFilteredPaintRegionIds as selectedFilteredPaintRegionIdsState,
  selectedFilteredWorkpackIds as selectedFilteredWorkpackIdsState,
  selectedTemplateFiltersState,
} from '@/components/Analysis/state';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useIsolateCustomBlock } from '../../../modules/CustomBlocks/hooks';
import { poiState } from '../../../modules/pointOfInterest';
const rowsPerPage = 30;

export const useComponentsTabData = () => {
  const [allComponentsSelected, setAllComponentsSelected] = useAtom(state.isAllComponentsSelected);
  const setSelectedComponentIds = useSetAtom(state.selectedAssemblyIds);
  const [loadMoreAssemblies, setLoadMoreAssemblies] = useState(true);
  const [page, setPage] = useState(0);
  const selectedComponentId = useAtomValue(state.selectedAssemblyId);
  const selectedTemplateFilters = useAtomValue(selectedTemplateFiltersState);
  const selectedDeckIds = useAtomValue(selectedFilteredDeckIdsState);
  const selectedPaintRegionIds = useAtomValue(selectedFilteredPaintRegionIdsState);
  const selectedWorkpackIds = useAtomValue(selectedFilteredWorkpackIdsState);
  const selectedCustomBlock = useAtomValue(customBlockState.selectedCustomBlock);
  const selectedSavedBlock = useAtomValue(customBlockState.selectedSavedBlock);
  const applyFilters = useAtomValue(state.applyFilters);
  const selectedAssemblyTemplateId = useSelectedAssemblyTemplateId();
  const [appliedAssemblyFilters, setAppliedAssemblyFilters] = useAtom(state.appliedAssemblyFilters);
  const allComponents = useAtomValue(state.filteredAssemblies);
  const [selectedAssemblyId, setSelectedAssemblyId] = useAtom(state.selectedAssemblyId);
  const setSelectedAssemblyName = useSetAtom(state.selectedAssemblyName);
  const setSelectedAssemblyInfo = useSetAtom(state.selectedAssemblyInfo);
  const [componentCount, setComponentCount] = useAtom(state.assembliesCount);
  const setSelectedTemplateFilters = useSetAtom(state.selectedTemplateFiltersState);
  const setSelectedPointOfInterestId = useSetAtom(poiState.selectedPointOfInterestId);
  const structureId = useGetStructureId();
  const [currentComponents, setcurrentComponents] = useState<Partial<Assembly>[]>([]);

  const handleSelectAllComponentsChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAllComponentsSelected(event.target.checked);
    if (event.target.checked) {
      setSelectedComponentIds(allComponents?.map((component) => component.id) ?? []);
    } else {
      setSelectedComponentIds([]);
    }
  };

  const [getAssemblies, { loading: componentsLoading }] =
    useGetAssembliesWithTemplateFieldDataLazyQuery({
      onCompleted: (assembliesWithTemplateDataResponse) => {
        setLoadMoreAssemblies(false);
        setComponentCount(assembliesWithTemplateDataResponse?.getAssembliesCount?.count ?? 0);
        setcurrentComponents((previousAssemblies) => {
          if (page === 0) {
            return assembliesWithTemplateDataResponse?.getAssembliesByTemplateFieldsFilters ?? [];
          }
          return [
            ...previousAssemblies,
            ...(assembliesWithTemplateDataResponse?.getAssembliesByTemplateFieldsFilters ?? []),
          ];
        });
      },
    });

  const fetchAssembliesData = useCallback(() => {
    if (structureId && selectedAssemblyTemplateId) {
      getAssemblies({
        variables: {
          input: {
            structureId,
            templateId: selectedAssemblyTemplateId,
            selectedTemplateFilters: appliedAssemblyFilters.templateFilters,
            selectedPaintRegionIds: [
              ...appliedAssemblyFilters.paintRegionFilters,
              ...(selectedCustomBlock?.paintRegionId ? [selectedCustomBlock.paintRegionId] : []),
              ...(selectedSavedBlock?.paintRegion?.id ? [selectedSavedBlock.paintRegion.id] : []),
            ],
            selectedDeckIds: appliedAssemblyFilters.deckFilters,
            selectedWorkpackIds: appliedAssemblyFilters.workpackFilters,
          },
          pageInfo: {
            skip: page * rowsPerPage,
            limit: rowsPerPage,
          },
        },
      });
      setLoadMoreAssemblies(false);
    }
  }, [
    structureId,
    selectedAssemblyTemplateId,
    appliedAssemblyFilters.templateFilters,
    appliedAssemblyFilters.paintRegionFilters,
    appliedAssemblyFilters.deckFilters,
    page,
    getAssemblies,
    selectedCustomBlock?.paintRegionId,
    appliedAssemblyFilters.workpackFilters,
    selectedSavedBlock?.paintRegion?.id,
  ]);

  const loadMore = () => {
    setPage((prevPage) => prevPage + 1);
  };

  useEffect(() => {
    fetchAssembliesData();
  }, [structureId, selectedAssemblyTemplateId, page, fetchAssembliesData, loadMoreAssemblies]);

  useEffect(() => {
    if (applyFilters) {
      setAppliedAssemblyFilters({
        templateFilters: selectedTemplateFilters,
        paintRegionFilters: selectedPaintRegionIds,
        deckFilters: selectedDeckIds,
        workpackFilters: selectedWorkpackIds,
      });
      setPage(0);
    }
  }, [
    applyFilters,
    selectedDeckIds,
    selectedPaintRegionIds,
    selectedTemplateFilters,
    selectedWorkpackIds,
    setAppliedAssemblyFilters,
  ]);

  const { data: assemblyContextualizedStatus, loading: contextloading } =
    useGetAssemblyContextualizedStatusQuery({
      variables: {
        assemblyId: selectedAssemblyId!,
      },
      skip: !selectedAssemblyId,
    });

  useEffect(() => {
    if (assemblyContextualizedStatus?.assembly?.isUncontextualized && !contextloading) {
      setSelectedAssemblyId(undefined);
      setSelectedAssemblyName(undefined);
      setSelectedAssemblyInfo(undefined);
      setSelectedTemplateFilters([]);
    }
  }, [
    assemblyContextualizedStatus?.assembly?.isUncontextualized,
    contextloading,
    setSelectedAssemblyId,
    setSelectedAssemblyInfo,
    setSelectedAssemblyName,
    setSelectedTemplateFilters,
  ]);

  const { isolateWorkScope } = useIsolateCustomBlock({ resetFocus: true });

  useSelectedPointOfInterestQuery({
    variables: {
      pointOfInterestId: selectedSavedBlock?.id ?? '',
    },

    skip: !selectedSavedBlock?.id,
    notifyOnNetworkStatusChange: true,
    onCompleted: (response) => {
      const data = response?.pointOfInterest;
      if (data) {
        isolateWorkScope(data as PointOfInterest);
      } else {
        setSelectedPointOfInterestId(undefined);
      }
    },
  });

  const initialLoading = useMemo(() => {
    return componentsLoading && page === 0;
  }, [componentsLoading, page]);

  return {
    initialLoading,
    componentsLoading,
    currentComponents,
    componentCount,
    selectedComponentId,
    allComponentsSelected,
    handleSelectAllComponentsChange,
    page,
    loadMore,
  };
};
